/**
 * A4 spec-mock-fidelity zod-gate — invoice wire-DTO (34-kör NaN-vakfolt megelőzése).
 *
 * Kontextus: a 34. körben a unit-tesztek ZÖLD voltak egy elavult kontraktuson
 * (subtotal/totalAmount/issueDate), mert a spec-mockok régi mezőneveket adtak.
 * A NaN-t csak a böngészős verify fogta meg. Ez a gate a drift-et merge-blokkolóan
 * kibuktatja: a kötelező pénz- és dátum-mezők HELYES nevének ellenőrzése alapján.
 *
 * Forrás (measured, #701-ben verifikált): GET /invoices + /invoices/:id
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// InvoiceItem wire-DTO
// ---------------------------------------------------------------------------

export const InvoiceItemWireSchema = z.object({
  id: z.string(),
  lineNumber: z.number(),
  description: z.string(),
  quantity: z.number(),
  unit: z.string(),
  // money-mezők (helyes nevek — a régi unitPrice/vatPercent/totalPrice drift-et fogja)
  unitPriceNet: z.number(),
  vatPercentage: z.number(),
  netAmount: z.number(),
  vatAmount: z.number(),
  grossAmount: z.number(),
  vatRate: z.string(),
  // opcionális mezők
  productId: z.string().optional(),
  vatCode: z.string().nullable().optional(),
  itemType: z.string().optional(),
  referenceType: z.string().optional(),
  referenceId: z.string().optional(),
});

export type InvoiceItemWire = z.infer<typeof InvoiceItemWireSchema>;

// ---------------------------------------------------------------------------
// Invoice wire-DTO
// ---------------------------------------------------------------------------

export const InvoiceWireSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  invoiceNumber: z.string(),
  // opcionális header-mezők
  prefix: z.string().optional(),
  sequenceNumber: z.number().nullable().optional(),
  partnerId: z.string(),
  type: z.string(),
  status: z.string(),
  // dátum-mezők (helyes nevek — a régi issueDate drift-et fogja)
  invoiceDate: z.string(),
  fulfillmentDate: z.string().optional(),
  dueDate: z.string(),
  // money-mezők (helyes nevek — a régi subtotal/totalAmount/balanceDue drift-et fogja)
  netAmount: z.number(),
  vatAmount: z.number(),
  grossAmount: z.number(),
  paidAmount: z.number(),
  currency: z.string(),
  // opcionális mezők
  paymentMethod: z.string().optional(),
  items: z.array(InvoiceItemWireSchema).optional(),
  // PR-C (#925) partner-mezők — Q-T128(a) wire-fidelity frissítés.
  // Flat: a toDomain MINDIG adja (partner-include nélkül üres string, taxNumber csak ha van).
  partnerName: z.string().optional(),
  partnerAddress: z.string().optional(),
  partnerTaxNumber: z.string().optional(),
  // Nested: CSAK a lista-endpoint partner-include-jával; name = cégnév-preferált
  // (companyName ?? name — invoice-mapping.ts toDomain, teszt-pinnelt).
  partner: z
    .object({
      id: z.string(),
      name: z.string(),
      taxNumber: z.string().optional(),
      address: z.string().optional(),
    })
    .optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type InvoiceWire = z.infer<typeof InvoiceWireSchema>;

// ---------------------------------------------------------------------------
// A4 additív bővítés — worksheet + stock wire-DTO-k (34-kör drift-osztály vetülete)
//
// Ugyanaz a gate-minta mint az invoice-nál: a VALÓS wire-mezőnevekre pinnelt zod,
// hogy a wire↔FE-normalizált / wire↔régi-konvenció drift merge-blokkolóan kibukjon.
// Konvenciók (#705): money→z.number() (nincs .positive()), dátum→z.string()
// (nincs .datetime() — wire-tolerancia), enum/státusz→z.string(), nested→.optional(),
// NINCS top-level .strict() (additív BE-bővítés biztonságos).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// WorksheetItem wire-DTO
// Forrás: apps/kgc-web/src/api/worksheets.ts `interface WorksheetItem`.
// ⚠️ A VALÓS mezőnév itt unitPrice/totalPrice (NEM az invoice-item
//    unitPriceNet/grossAmount konvenciója — külön kontraktus).
// ---------------------------------------------------------------------------

export const WorksheetItemWireSchema = z.object({
  id: z.string(),
  worksheetId: z.string(),
  // itemType wire-enum: PART | LABOR | EXTERNAL_COST | OTHER (z.string() — wire-tolerancia)
  itemType: z.string(),
  description: z.string(),
  quantity: z.number(),
  // money-mezők — a VALÓS worksheet-item nevek (unitPrice/totalPrice)
  unitPrice: z.number(),
  totalPrice: z.number(),
  // opcionális
  productId: z.string().optional(),
});

export type WorksheetItemWire = z.infer<typeof WorksheetItemWireSchema>;

// ---------------------------------------------------------------------------
// Worksheet (detail) wire-DTO
// Forrás: packages/service/service-worksheet/src/dto/worksheet.dto.ts
//         `WorksheetDetailReadSchema` + apps/kgc-web/src/api/worksheets.ts
//         `interface Worksheet`.
//
// ⚠️ KORREKCIÓ (Q-T180, 2026-07-30): itt korábban ez állt — *„Az `inspectionFee`
//    SZÁNDÉKOSAN kimarad — az csak a FE-interfészen él, a detail read-DTO-n
//    nincs."* Ez a premissza MEGDŐLT: a read-DTO mostantól DEKLARÁLJA mindkét
//    díj-mezőt, a BE-mapper pedig kitölti őket. A „szándékos kimaradás" ezért nem
//    tervezési döntés volt, hanem egy hiányzó BE-réteg következménye — és amíg
//    fennállt, a `PaymentGateBanner` falsy-ága miatt a fizetési-kapu banner
//    SOSEM jelent meg. A mezők most a sémában vannak (lásd lentebb).
// ---------------------------------------------------------------------------

export const WorksheetWireSchema = z.object({
  // kötelező header / azonosító mezők
  id: z.string(),
  tenantId: z.string(),
  worksheetNumber: z.string(),
  type: z.string(),
  status: z.string(),
  priority: z.string(),
  partnerId: z.string(),
  deviceName: z.string(),
  faultDescription: z.string(),
  createdBy: z.string(),
  // dátum-mezők (kötelező, wire: ISO-8601 string)
  createdAt: z.string(),
  updatedAt: z.string(),
  receivedAt: z.string(),
  // opcionális szöveg-mezők
  deviceSerialNumber: z.string().optional(),
  diagnosis: z.string().optional(),
  internalNote: z.string().optional(),
  // money-mezők (opcionális)
  costLimit: z.number().optional(),
  /**
   * Q-T180: a bevizsgálási díj. A BE-mapper (`toWorksheetDomain`) 2026-07-30-tól
   * kitölti, a BE read-séma is deklarálja — enélkül a sor a zod-strip miatt
   * CSENDBEN kiesett, és a `PaymentGateBanner` falsy-ága miatt a fizetési-kapu
   * banner sosem jelent meg. A FE-interfész (`worksheets.ts:86-87`) végig ismerte
   * mindkét mezőt: a rés kizárólag a wire-sémán volt.
   */
  inspectionFee: z.number().nullable().optional(),
  inspectionFeePaid: z.boolean().optional(),
  storageDailyFee: z.number().optional(),
  storageTotalFee: z.number().optional(),
  effectiveHourlyRate: z.number().optional(),
  // lifecycle / dátum-mezők (opcionális, wire: ISO-8601 string)
  estimatedCompletionDate: z.string().optional(),
  completedAt: z.string().optional(),
  diagnosedAt: z.string().optional(),
  startedAt: z.string().optional(),
  deliveredAt: z.string().optional(),
  customerNotifiedAt: z.string().optional(),
  closedAt: z.string().optional(),
  storageStartDate: z.string().optional(),
  // nested tételek (a /worksheets/:id/items seam-ből, opcionális)
  items: z.array(WorksheetItemWireSchema).optional(),
});

export type WorksheetWire = z.infer<typeof WorksheetWireSchema>;

// ---------------------------------------------------------------------------
// WarehouseStockBreakdown wire-DTO (StockSummary.byWarehouse[] elem)
// Forrás: apps/kgc-web/src/api/inventory.ts `interface WarehouseStockBreakdown`.
// A mezőnevek 1:1 a parent-tel (*Quantity) — sum-invariáns: parent = Σ byWarehouse.
// ---------------------------------------------------------------------------

export const WarehouseStockBreakdownWireSchema = z.object({
  warehouseId: z.string(),
  warehouseName: z.string(),
  totalQuantity: z.number(),
  availableQuantity: z.number(),
  reservedQuantity: z.number(),
  inTransitQuantity: z.number(),
  inServiceQuantity: z.number(),
  rentedQuantity: z.number(),
});

export type WarehouseStockBreakdownWire = z.infer<typeof WarehouseStockBreakdownWireSchema>;

// ---------------------------------------------------------------------------
// StockSummary wire-DTO (GET /inventory/stock-summaries)
// Forrás: packages/shared/inventory/src/interfaces/inventory.interface.ts
//         `interface StockSummary` + apps/kgc-web/src/api/inventory.ts
//         `interface FourFieldStockSummary` (1:1). A `lastUpdated` a wire-en
//         ISO-8601 string (a BE-interfészen Date, serialize-kor string).
// ---------------------------------------------------------------------------

export const StockSummaryWireSchema = z.object({
  tenantId: z.string(),
  productId: z.string(),
  productName: z.string(),
  // 6-mezős stock-modell (mind kötelező z.number())
  totalQuantity: z.number(),
  availableQuantity: z.number(),
  reservedQuantity: z.number(),
  inTransitQuantity: z.number(),
  inServiceQuantity: z.number(),
  rentedQuantity: z.number(),
  unit: z.string(),
  // stockLevelStatus wire-enum: OK | LOW | CRITICAL | OUT_OF_STOCK (z.string())
  stockLevelStatus: z.string(),
  lastUpdated: z.string(),
  // opcionális
  warehouseId: z.string().optional(),
  minStockLevel: z.number().optional(),
  // value-trail (26-kör read-DTO bővítés, opcionális)
  totalValue: z.number().optional(),
  byWarehouse: z.array(WarehouseStockBreakdownWireSchema).optional(),
});

export type StockSummaryWire = z.infer<typeof StockSummaryWireSchema>;

// ---------------------------------------------------------------------------
// StockMovement wire-DTO (audit-trail mozgás-rekord)
// ⚠️⚠️ A VALÓS WIRE shape-re pinnelt, NEM a FE-normalizáltra!
// Forrás: packages/shared/inventory/src/interfaces/movement.interface.ts
//         `interface InventoryMovement` + apps/kgc-web/src/api/inventory.ts
//         `interface BackendMovement`.
// A FE `normalizeMovement()` adapter ezt {type:'IN'|'OUT', quantity, previousStock,
// newStock} alakra konvertálja — az NEM a wire. A gate a valós quantityChange /
// previousQuantity / newQuantity mezőkre pinnel (kötelező), így a normalizált
// shape véletlen wire-ként parse-olása merge-blokkolóan kibukik.
// ---------------------------------------------------------------------------

export const StockMovementWireSchema = z.object({
  // type wire-enum: RECEIPT | ISSUE | TRANSFER_OUT | TRANSFER_IN | ADJUSTMENT |
  //   RETURN | SCRAP | RESERVATION | RELEASE | STATUS_CHANGE (z.string() — wire-tolerancia)
  type: z.string(),
  // mennyiség-mezők — a VALÓS wire-nevek (NEM a normalizált `quantity`)
  quantityChange: z.number(),
  previousQuantity: z.number(),
  newQuantity: z.number(),
  unit: z.string(),
  // dátum-mezők (kötelező, wire: ISO-8601 string)
  performedAt: z.string(),
  createdAt: z.string(),
  // money / opcionális mezők
  unitCost: z.number().optional(),
  value: z.number().optional(),
  referenceType: z.string().optional(),
  referenceId: z.string().optional(),
  serialNumber: z.string().optional(),
});

export type StockMovementWire = z.infer<typeof StockMovementWireSchema>;

// ---------------------------------------------------------------------------
// InvoiceStatsWireSchema — /invoices/stats aggregált wire (34-kör NaN-osztály
// stats-vetülete; a kimaradt darab az invoice/worksheet/stock wire-gate után).
// Forrás (measured, Zeno #714-verifikált): GET /invoices/stats →
// controller `return { data: stats }` ({data}-wrap). Mind a 7 mező JS-number a
// wire-en: count-mezők (groupBy _count + reduce); totalRevenue/unpaidTotal a
// repo-ban explicit Math.round(Number(...)) → plain JS-number MÁR a serializáció
// ELŐTT, NEM Prisma.Decimal-string (#708 Decimal-coercion-tanulság BE-oldalon).
// A z.number() a (hiányzó vagy string-serializált) money-mező drift-et fogja —
// különben a money-kártya (formatPrice(totalRevenue)) némán NaN-t renderelne.
// ---------------------------------------------------------------------------

export const InvoiceStatsWireSchema = z.object({
  total: z.number(),
  draft: z.number(),
  sent: z.number(),
  paid: z.number(),
  overdue: z.number(),
  // money-mezők — JS-number a wire-en (repo Math.round(Number()), NEM Decimal-string)
  totalRevenue: z.number(),
  unpaidTotal: z.number(),
});

export type InvoiceStatsWire = z.infer<typeof InvoiceStatsWireSchema>;

// ---------------------------------------------------------------------------
// GoodsReceipt (Beszerzés) wire-DTO-k — 36-kör golden-thread proto-parity gate.
//
// Kontextus: a 34-kör NaN-vakfolt Beszerzés-vetülete. A goods-receipt FE-oldal
// (List/Form/Detail) money-mezős (unitPrice + shipping-allokáció), de a wire NEM
// volt zod-pinnelve. A measured-recon (2026-06-30) ÉLŐ wrapper-bugot is feltárt:
// a detail/items/create wire DIREKT volt (nincs {data}), a FE viszont res.data-t
// unwrap-ol → undefined → törött DetailPage/create-flow. A BE-fix (#718) a wire-t
// a {data}-target-shape-re igazítja; ez a gate arra pinnel + a regressziót
// (direkt-shape visszacsúszás) merge-blokkolóan kibuktatja.
//
// Forrás (measured, BE-verifikált): apps/kgc-web/src/api/goods-receipt.ts
//   interface AvizoItem/Avizo/ReceiptItem/Receipt/ShippingAllocationItem/
//   ShippingDistributionResult + apps/kgc-api/.../goods-receipt.controller.ts.
// A status-mezők z.string() (wire-tolerancia, StockMovement-minta); a money- és
// mennyiség-mezők z.number() (a NaN-drift ellen).
// ---------------------------------------------------------------------------

export const AvizoItemWireSchema = z.object({
  id: z.string(),
  purchaseAdviceId: z.string(),
  productId: z.string(),
  productCode: z.string(),
  productName: z.string(),
  expectedQuantity: z.number(),
  // money-mező
  unitPrice: z.number(),
});

export type AvizoItemWire = z.infer<typeof AvizoItemWireSchema>;

export const AvizoWireSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  // wire: ISO-8601 string
  expectedDate: z.string(),
  // status wire-enum: PENDING | RECEIVED | CANCELLED (z.string() — wire-tolerancia)
  status: z.string(),
  items: z.array(AvizoItemWireSchema),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // opcionális
  notes: z.string().optional(),
  pdfUrl: z.string().optional(),
});

export type AvizoWire = z.infer<typeof AvizoWireSchema>;

export const ReceiptItemWireSchema = z.object({
  id: z.string(),
  receiptId: z.string(),
  productId: z.string(),
  productCode: z.string(),
  productName: z.string(),
  // mennyiség-mezők (kötelező z.number())
  expectedQuantity: z.number(),
  receivedQuantity: z.number(),
  // money-mező
  unitPrice: z.number(),
  // opcionális
  avizoItemId: z.string().optional(),
  locationCode: z.string().optional(),
  // purchaseCategory wire-enum: ARU | BERUHAZAS | BERENDEZES | EGYEB | REZSI
  purchaseCategory: z.string().optional(),
  packageQuantity: z.number().optional(),
  unitsPerPackage: z.number().optional(),
});

export type ReceiptItemWire = z.infer<typeof ReceiptItemWireSchema>;

export const ReceiptWireSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  receiptNumber: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  // status wire-enum: DRAFT | IN_PROGRESS | DISCREPANCY | COMPLETED (z.string())
  status: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // opcionális
  purchaseAdviceId: z.string().optional(),
  notes: z.string().optional(),
  // a list endpoint omittálja, a detail tölti (goods-receipt.ts:82-85)
  items: z.array(ReceiptItemWireSchema).optional(),
  completedAt: z.string().optional(),
  completedBy: z.string().optional(),
});

export type ReceiptWire = z.infer<typeof ReceiptWireSchema>;

// ShippingAllocationItem — a distribute-shipping COMPUTED result eleme.
// Forrás: goods-receipt.controller.ts:453-470 (Math.round → number) + FE
// interface ShippingAllocationItem. MIND number (a money-surface a DetailPage
// :356-365-ön renderelődik → L1-pin).
export const ShippingAllocationItemWireSchema = z.object({
  receiptItemId: z.string(),
  productCode: z.string(),
  productName: z.string(),
  originalUnitPrice: z.number(),
  itemTotalValue: z.number(),
  proportion: z.number(),
  shippingAllocation: z.number(),
  adjustedUnitPrice: z.number(),
});

export type ShippingAllocationItemWire = z.infer<typeof ShippingAllocationItemWireSchema>;

// ShippingDistributionResult — POST /receipts/:id/distribute-shipping `data`-payload.
// Wire-kulcs `items` (NEM `allocations` — az a BE belső változó, controller :478
// `items: allocations`). MIND number.
export const ShippingDistributionWireSchema = z.object({
  receiptId: z.string(),
  shippingCost: z.number(),
  receiptTotalValue: z.number(),
  items: z.array(ShippingAllocationItemWireSchema),
});

export type ShippingDistributionWire = z.infer<typeof ShippingDistributionWireSchema>;

// LateFeeDetails — a return-flow késedelmi-díj bontása (39-kör FE-4, #737/#738/#739).
// Forrás: @kgc/rental-pricing daily-late-fee.ts DailyLateFeeResult (SSoT, #738); a #739
// read-expozíció pinnelt wire-e: GET /rentals/:id → data.lateFee? (visszavett/számolt
// késedelmes bérlésen, egyébként undefined — a régi 50%-os shape-et a BE nem tükrözi).
// dailyLateFeeRate = az EFFEKTÍV napi tétel (MIN_RENTAL_FEE-nél már max(napi díj, padló)
// eredménye — a FE NEM számol, csak formáz). feeModel optional: a #738 előtti régi
// breakdown-rekordokban nincs → a fogyasztó safeParse-gate-tel degradál (#718-guard).
export const LateFeeDetailsWireSchema = z.object({
  daysOverdue: z.number(),
  dailyLateFeeRate: z.number(),
  feeModel: z.enum(['MIN_RENTAL_FEE', 'DAILY_RATE_PERCENTAGE']).optional(),
  amount: z.number(),
  maxLateFee: z.number(),
  // #739 read-mezők — optional: a #738-as (read-fix előtti) breakdown-rekordokban nincsenek
  isWaived: z.boolean().optional(),
  calculatedAt: z.string().optional(), // Date→string wire (#701-osztály)
});

export type LateFeeDetailsWire = z.infer<typeof LateFeeDetailsWireSchema>;

// ── Settlement-preview (40-kör PV-BERLES-003, PR-5 Zod-pin 2026-07-02) ──
// POST /rentals-direct/:id/settlement-preview → {data: SettlementPreview}.
// LOKÁL pin-tükör: a PR-5 @kgc/api-contract sémájának landolása után import-csere
// (single-source, V65 bank-zod minta) — a shape a csatornán pinnelt kontraktus 1:1.
// Minden összeg EGÉSZ Ft (int). A `label` BE-generált — a FE rendereli, nem gyártja.

export const SettlementLineWireSchema = z.object({
  rentalItemId: z.string().nullable(), // LATE_FEE-nél null (bérlés-szintű)
  equipmentName: z.string().nullable(),
  kind: z.enum(['DAMAGE', 'MISSING_ACCESSORY', 'CLEANING', 'LATE_FEE']),
  label: z.string(),
  amount: z.number().int(),
  vatRate: z.number().nullable(), // null = »ÁFA egyeztetés alatt« badge (R-4)
});

export const SettlementDepositWireSchema = z.object({
  depositId: z.string(),
  rentalItemId: z.string().nullable(), // null = legacy per-rental
  amount: z.number().int(),
  paymentMethod: z.enum(['CASH', 'CARD', 'BANK_TRANSFER', 'PRE_AUTH']), // READ-enum UPPERCASE!
  nettable: z.boolean(), // false = CARD/PRE_AUTH → KÜLÖN blokk, SOSEM nettózva
  preAuthExpiresAt: z.string().nullable(),
  forceImmediateSettle: z.boolean(), // R-2 hard-guard
});

export const SettlementPreviewWireSchema = z.object({
  rentalId: z.string(),
  calculatedAt: z.string(),
  currency: z.literal('HUF'),
  lines: z.array(SettlementLineWireSchema),
  deposits: z.array(SettlementDepositWireSchema),
  cashFlow: z.object({
    deductionsTotal: z.number().int(),
    depositApplied: z.number().int(),
    refundToCustomer: z.number().int(),
    payableByCustomer: z.number().int(),
  }),
  cardFlow: z
    .object({
      invoiceTotal: z.number().int(),
      myposCaptureTotal: z.number().int(),
      myposRefundTotal: z.number().int(),
    })
    .nullable(), // null = nincs kártyás/preAuth kaució
});

export type SettlementLineWire = z.infer<typeof SettlementLineWireSchema>;
export type SettlementDepositWire = z.infer<typeof SettlementDepositWireSchema>;
export type SettlementPreviewWire = z.infer<typeof SettlementPreviewWireSchema>;
